import { useCallback, useEffect, useState, type ReactNode } from 'react';

import { Button } from '@/components/design-system/Button';
import { useDebounce } from '@/hooks/useDebounce';
import { ExpandAltOutlined, ShrinkOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { ColumnPicker, type ColumnDefinition } from './ColumnPicker';
import { GridSearchInput } from './GridSearchInput';

interface GridToolbarProps {
  isTree?: boolean;
  columns: ColumnDefinition[];
  visibleColumns: string[];
  onVisibilityChange: (visibleColumnIds: string[]) => void;
  onExpandAll?: () => void;
  onCollapseAll?: () => void;
  onQuickFilterChange: (quickFilterText: string) => void;
  toolbarActions?: ReactNode;
  showSearchBar?: boolean;
  showColumnPicker?: boolean;
  showExpandCollapse?: boolean;
}

export const GridToolbar = ({
  isTree = false,
  columns,
  visibleColumns,
  onVisibilityChange,
  onExpandAll,
  onCollapseAll,
  onQuickFilterChange,
  toolbarActions,
  showSearchBar = true,
  showColumnPicker = true,
  showExpandCollapse = true
}: GridToolbarProps) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 300);
  const quickFilterText = debouncedSearchText.length >= 2 ? debouncedSearchText : '';

  useEffect(() => {
    onQuickFilterChange(quickFilterText);
  }, [quickFilterText, onQuickFilterChange]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  return (
    <div className="border-border bg-background flex shrink-0 items-center gap-2 border-b py-2">
      <div className="flex min-w-0 flex-1 items-center gap-2">
        {showSearchBar && (
          <GridSearchInput value={searchText} onChange={handleSearchChange} className="min-w-0 flex-1" />
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {showColumnPicker && (
          <ColumnPicker
            columns={columns}
            visibleColumns={visibleColumns}
            onVisibilityChange={onVisibilityChange}
            data-testid={isTree ? 'tree-grid-column-picker' : 'data-grid-column-picker'}
          />
        )}
        {isTree && showExpandCollapse && (
          <>
            <Button
              variant="default"
              size="sm"
              className="border-border-100"
              onClick={onExpandAll}
              data-testid="tree-grid-expand-all"
            >
              <ExpandAltOutlined />
              {t('actions.expandAll')}
            </Button>
            <Button
              variant="default"
              size="sm"
              className="border-border-100"
              onClick={onCollapseAll}
              data-testid="tree-grid-collapse-all"
            >
              <ShrinkOutlined />
              {t('actions.collapseAll')}
            </Button>
          </>
        )}
        {toolbarActions}
      </div>
    </div>
  );
};
